import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePagesTranslation } from "../../../../pages/locales";
import AuthService from "../../../../services/AuthService";
import {
    getSlidingStoryTitle,
    pickSlidingOriginalImageUrl,
    pickSlidingPlayImageUrl,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import LibraryCard from "../components/LibraryCard";
import LibraryCardSkeleton from "../components/LibraryCardSkeleton";
import LibraryCategoryPills from "../components/LibraryCategoryPills";
import LibraryFeedState from "../components/LibraryFeedState";
import PageShell from "../components/PageShell";
import {
    beginPlayFromSlidingId,
    fetchBrowsePage,
    fetchDistinctTags,
    fetchInProgressBrowseKeys,
    filterBrowseGroupsExcludingInProgress,
    groupHasPlayableTemplate,
    getSnapshotLibraryFixtureGroup,
    getSnapshotLibraryFixtureStoryTitle,
    getSnapshotLibraryTags,
    resolveSlidingBoardPreview,
    SNAPSHOT_LIBRARY_SELECTED_TAG,
    tagLabelFromRow,
} from "../lib/api";
import {
    buildLocalPlayNavigateState,
    isSnapshotUiTesting,
    notifyNativeSnapshotLibraryReady,
    notifyNativeSnapshotLibraryTagReady,
    SNAPSHOT_LIBRARY_READY_LABEL,
    snapshotLibraryTagReadyLabel,
} from "../lib/access";
import { useSubscription } from "../lib/SubscriptionContext";
import {
    saveAuthenticatedPlayGridPreferences,
    subscribeAuthenticatedPlayGridPreferences,
} from "../lib/playGridPreferences";
import PaywallSheet from "../components/PaywallSheet";
import { isLikelyOfflineError, useOnlineStatus } from "../lib/networkState";
import { captionSx } from "../styles/mainStyles";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { slidingPageContentSx } from "../lib/appearance";
import { attachPlayReturnPath, navigateToPlay } from "../lib/playRouteState";
import {
    chargePremiumPlayCreditOnEntry,
    isInsufficientCreditError,
    resolvePremiumPlayCreditContext,
} from "../lib/slidingCreditConsume";
import Logger from "../../../../services/Logger";
import { LIBRARY_PATH, PLAY_PATH, SETTINGS_PATH } from "../routes";

const PAGE_SIZE = 12;
const INITIAL_SKELETON_COUNT = 8;

/** Library browse grid: 2 columns portrait, 5 columns landscape. */
const libraryPuzzleGridSx = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 1.25,
    alignItems: "stretch",
    "@media (orientation: landscape)": {
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    },
};

/** Display tag: title case, no count — e.g. "animal" → "Animal". */
function formatTagLabel(raw) {
    const s = tagLabelFromRow(raw);
    if (!s) return "";
    return s
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

export default function LibraryPage() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const snapshotMode = isSnapshotUiTesting();
    const { tokens } = useSlidingAppearance();
    const { access, isSubscribed, isConfigurationLocked, credits } = useSubscription();
    const [showPaywallSheet, setShowPaywallSheet] = useState(false);
    const [savedGridPrefs, setSavedGridPrefs] = useState(null);
    const online = useOnlineStatus();
    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);

    const [tags, setTags] = useState(() => (snapshotMode ? getSnapshotLibraryTags() : []));
    const [tagsLoading, setTagsLoading] = useState(() => !snapshotMode);
    const [selectedTag, setSelectedTag] = useState(() =>
        snapshotMode ? SNAPSHOT_LIBRARY_SELECTED_TAG : null
    );
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [rows, setRows] = useState(() =>
        snapshotMode ? [getSnapshotLibraryFixtureGroup(SNAPSHOT_LIBRARY_SELECTED_TAG)] : []
    );
    const [loading, setLoading] = useState(() => !snapshotMode);
    const [loadingMore, setLoadingMore] = useState(false);
    const [startingStoryId, setStartingStoryId] = useState(null);
    const [error, setError] = useState(null);
    /** @type {'offline' | 'fetch' | null} */
    const [errorKind, setErrorKind] = useState(null);

    useEffect(() => {
        if (snapshotMode) return undefined;
        return subscribeAuthenticatedPlayGridPreferences(setSavedGridPrefs);
    }, [snapshotMode]);

    const loadingRef = useRef(false);
    const wasOfflineRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const hasNextRef = useRef(false);
    const pageRef = useRef(0);
    const selectedTagRef = useRef(null);

    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);
    useEffect(() => {
        loadingMoreRef.current = loadingMore;
    }, [loadingMore]);
    useEffect(() => {
        hasNextRef.current = hasNext;
    }, [hasNext]);
    useEffect(() => {
        pageRef.current = page;
    }, [page]);
    useEffect(() => {
        selectedTagRef.current = selectedTag;
    }, [selectedTag]);

    useEffect(() => {
        if (snapshotMode) {
            setTags(getSnapshotLibraryTags());
            setTagsLoading(false);
            return undefined;
        }
        let cancelled = false;
        setTagsLoading(true);
        fetchDistinctTags()
            .then((list) => {
                if (!cancelled) setTags(list);
            })
            .catch((err) => {
                if (!cancelled) {
                    setTags([]);
                    if (isLikelyOfflineError(err)) {
                        setErrorKind("offline");
                        setError(
                            t("native.library.offlineBody", {
                                defaultValue: "Connect to the internet to browse the puzzle library.",
                            })
                        );
                    }
                }
            })
            .finally(() => {
                if (!cancelled) setTagsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [t, snapshotMode]);

    const fetchPage = useCallback(async (pageIndex, tag, append) => {
        const paged = await fetchBrowsePage({
            page: pageIndex,
            pageSize: PAGE_SIZE,
            sort: "publishedAt:desc",
            tag: tag || undefined,
            excludeCompleted: true,
        });
        if (typeof console !== "undefined") {
            console.info("[Library] browse page (normalized)", {
                page: {
                    size: paged.size,
                    number: paged.number,
                    totalElements: paged.totalElements,
                    totalPages: paged.totalPages,
                },
                hasNext: paged.hasNext,
                contentLength: (paged.content || []).length,
                tag: tag || null,
            });
        }
        let content = paged.content || [];
        if (await AuthService.isAuthenticated()) {
            const inProgressKeys = await fetchInProgressBrowseKeys();
            content = filterBrowseGroupsExcludingInProgress(content, inProgressKeys);
            content = content.filter((g) => groupHasPlayableTemplate(g, inProgressKeys));
        }
        if (typeof console !== "undefined") {
            console.info("[Library] browse page (after client filter)", {
                pageIndex,
                rowCount: content.length,
                tag: tag || null,
            });
        }
        const next =
            paged.hasNext === true ||
            (typeof paged.totalPages === "number" &&
                pageIndex + 1 < Math.max(0, paged.totalPages));
        if (append) {
            setRows((prev) => [...prev, ...content]);
        } else {
            setRows(content);
        }
        setHasNext(next);
        setPage(paged.number ?? pageIndex);
        return content;
    }, []);

    const loadInitial = useCallback(
        async (tag) => {
            setLoadingMore(false);
            setError(null);
            setErrorKind(null);
            setHasNext(false);
            setPage(0);
            if (scrollRef.current) {
                scrollRef.current.scrollTop = 0;
            }
            if (snapshotMode) {
                setRows([getSnapshotLibraryFixtureGroup(tag)]);
                setError(null);
                setErrorKind(null);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const content = await fetchPage(0, tag, false);
                if (!content || content.length === 0) {
                    /* empty library */
                }
            } catch (err) {
                const offline = isLikelyOfflineError(err);
                setErrorKind(offline ? "offline" : "fetch");
                setError(
                    offline
                        ? t("native.library.offlineBody", {
                              defaultValue:
                                  "Connect to the internet to browse the puzzle library.",
                          })
                        : t("native.library.errorBody", {
                              defaultValue:
                                  "Something went wrong. Check your connection and try again.",
                          })
                );
                setRows([]);
            } finally {
                setLoading(false);
            }
        },
        [fetchPage, snapshotMode]
    );

    const loadNextPage = useCallback(async () => {
        if (loadingRef.current || loadingMoreRef.current || !hasNextRef.current) return;
        setLoadingMore(true);
        setError(null);
        try {
            await fetchPage(pageRef.current + 1, selectedTagRef.current, true);
        } catch (err) {
            const offline = isLikelyOfflineError(err);
            setErrorKind(offline ? "offline" : "fetch");
            setError(
                offline
                    ? t("native.library.offlineBody", {
                          defaultValue: "Connect to the internet to browse the puzzle library.",
                      })
                    : t("native.library.loadMoreError", {
                          defaultValue: "Couldn't load more puzzles.",
                      })
            );
        } finally {
            setLoadingMore(false);
        }
    }, [fetchPage, t]);

    useEffect(() => {
        if (snapshotMode) return;
        void loadInitial(selectedTag);
    }, [selectedTag, loadInitial, snapshotMode, location.key]);

    useEffect(() => {
        if (!online) {
            wasOfflineRef.current = true;
            return;
        }
        if (wasOfflineRef.current && errorKind === "offline" && !loading) {
            wasOfflineRef.current = false;
            void loadInitial(selectedTag);
        }
    }, [online, errorKind, loading, selectedTag, loadInitial]);

    const retryLoad = () => {
        void loadInitial(selectedTag);
    };

    const categoryOptions = snapshotMode
        ? tags
              .map((row) => {
                  const filterValue = tagLabelFromRow(row);
                  if (!filterValue) return null;
                  return { value: filterValue, label: formatTagLabel(row) };
              })
              .filter(Boolean)
        : [
              { value: null, label: t("native.library.all", { defaultValue: "All" }) },
              ...tags
                  .map((row) => {
                      const filterValue = tagLabelFromRow(row);
                      if (!filterValue) return null;
                      return { value: filterValue, label: formatTagLabel(row) };
                  })
                  .filter(Boolean),
          ];

    const snapshotTagReadyLabel =
        snapshotMode && selectedTag && !loading && rows.length > 0
            ? snapshotLibraryTagReadyLabel(selectedTag)
            : null;

    useLayoutEffect(() => {
        if (!snapshotMode) return undefined;
        notifyNativeSnapshotLibraryReady();
        const timer = window.setTimeout(() => notifyNativeSnapshotLibraryReady(), 500);
        return () => window.clearTimeout(timer);
    }, [snapshotMode, selectedTag]);

    useEffect(() => {
        if (!snapshotMode || loading || rows.length === 0 || !selectedTag) return undefined;
        notifyNativeSnapshotLibraryTagReady(selectedTag);
        const timer = window.setTimeout(() => notifyNativeSnapshotLibraryTagReady(selectedTag), 350);
        return () => window.clearTimeout(timer);
    }, [snapshotMode, loading, rows.length, selectedTag]);

    useEffect(() => {
        const root = scrollRef.current;
        const sentinel = sentinelRef.current;
        if (!root || !sentinel) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                loadNextPage();
            },
            { root, rootMargin: "80px", threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadNextPage, selectedTag, loading]);

    const selectTag = (tag) => {
        setSelectedTag(tag);
    };

    /** Instant fixture swap for native snapshot tag selectors (no API / scroll). */
    const applySnapshotLibraryTag = useCallback((tag) => {
        const slug =
            tag != null && String(tag).trim()
                ? String(tag).trim().toLowerCase()
                : SNAPSHOT_LIBRARY_SELECTED_TAG;
        setSelectedTag(slug);
        setRows([getSnapshotLibraryFixtureGroup(slug)]);
        setLoading(false);
        setLoadingMore(false);
        setError(null);
        setErrorKind(null);
        setHasNext(false);
        setPage(0);
        notifyNativeSnapshotLibraryTagReady(slug);
        window.setTimeout(() => notifyNativeSnapshotLibraryTagReady(slug), 250);
    }, []);

    useEffect(() => {
        if (!snapshotMode) return undefined;
        window.__PUZZLE_APPLY_SNAPSHOT_LIBRARY_TAG__ = applySnapshotLibraryTag;
        const onTagEvent = (event) => {
            const rawTag = event?.detail?.tag;
            if (typeof rawTag === "string" && rawTag.trim()) {
                applySnapshotLibraryTag(rawTag);
            }
        };
        window.addEventListener("puzzle-snapshot-select-tag", onTagEvent);
        return () => {
            delete window.__PUZZLE_APPLY_SNAPSHOT_LIBRARY_TAG__;
            window.removeEventListener("puzzle-snapshot-select-tag", onTagEvent);
        };
    }, [snapshotMode, applySnapshotLibraryTag]);

    const startPlay = async (group, { slidingId, gridSize, difficulty }) => {
        const storyId = group?.storyId ?? group?.story?.id;
        setStartingStoryId(storyId ?? slidingId);
        setError(null);
        try {
            if (isConfigurationLocked(gridSize, difficulty)) {
                setShowPaywallSheet(true);
                return;
            }
            void saveAuthenticatedPlayGridPreferences({ gridSize, difficulty });
            const authed = await AuthService.isAuthenticated();
            const boardPreview = resolveSlidingBoardPreview(group, gridSize, difficulty);
            if (!authed) {
                navigateToPlay(
                    navigate,
                    PLAY_PATH,
                    attachPlayReturnPath(
                        buildLocalPlayNavigateState({
                            slidingId,
                            gridSize,
                            difficulty,
                            story: group?.story,
                            storyTitle: getSlidingStoryTitle(group?.story),
                            imageUrl: pickSlidingPlayImageUrl(group?.story),
                            previewImageUrl: pickSlidingOriginalImageUrl(group?.story),
                            layout: boardPreview?.startingLayout,
                            resume: Boolean(boardPreview?.startingLayout),
                        }),
                        LIBRARY_PATH,
                    ),
                );
                return;
            }
            const result = await beginPlayFromSlidingId(slidingId, {
                gridSize,
                difficulty,
                imageUrl: pickSlidingPlayImageUrl(group?.story),
                story: group?.story,
                storyTitle: getSlidingStoryTitle(group?.story),
                previewImageUrl: pickSlidingOriginalImageUrl(group?.story),
                returnPath: LIBRARY_PATH,
            });
            if (result.error) {
                setError(result.error);
            }
            const playState =
                result.state ||
                {
                    slidingId,
                    gridSize,
                    difficulty,
                    imageUrl: pickSlidingPlayImageUrl(group?.story),
                    storyTitle: getSlidingStoryTitle(group?.story),
                    previewImageUrl: pickSlidingOriginalImageUrl(group?.story),
                };
            if (playState?.slidingId) {
                try {
                    const creditCtx = await resolvePremiumPlayCreditContext({ access, credits });
                    await chargePremiumPlayCreditOnEntry({
                        slidingId: playState.slidingId,
                        gridSize,
                        difficulty,
                        hasLegacyUnlimitedAccess: creditCtx.hasLegacyUnlimitedAccess,
                        hasCredits: creditCtx.hasCredits,
                        applyCreditBalance: credits.applyCreditBalance,
                    });
                } catch (e) {
                    if (isInsufficientCreditError(e)) {
                        navigate(SETTINGS_PATH);
                        return;
                    }
                    Logger.warn("LibraryPage.startPlay credit charge", e);
                }
            }
            navigateToPlay(
                navigate,
                PLAY_PATH,
                attachPlayReturnPath(playState, LIBRARY_PATH),
            );
        } finally {
            setStartingStoryId(null);
        }
    };

    return (
        <PageShell
            scrollRef={scrollRef}
            showBrandHeader={false}
            pageHero="library"
            pageTitle={t("native.library.title", { defaultValue: "Library" })}
        >
            {snapshotMode ? (
                <Typography
                    component="span"
                    aria-label={SNAPSHOT_LIBRARY_READY_LABEL}
                    sx={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clip: "rect(0,0,0,0)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {SNAPSHOT_LIBRARY_READY_LABEL}
                </Typography>
            ) : null}
            {snapshotTagReadyLabel ? (
                <Typography
                    component="span"
                    aria-label={snapshotTagReadyLabel}
                    sx={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clip: "rect(0,0,0,0)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {snapshotTagReadyLabel}
                </Typography>
            ) : null}
            {snapshotMode && selectedTag && !loading && rows.length > 0 ? (
                <Typography
                    component="span"
                    aria-label={`PUZZLE_SNAPSHOT_LIBRARY_STORY_${String(selectedTag).trim().toLowerCase()}`}
                    sx={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clip: "rect(0,0,0,0)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {getSnapshotLibraryFixtureStoryTitle(selectedTag)}
                </Typography>
            ) : null}
            <Box
                sx={{
                    ...slidingPageContentSx(tokens),
                    bgcolor: "transparent",
                    background: "transparent",
                }}
            >
            {tagsLoading ? (
                <Box sx={{ display: "flex", gap: 1, mb: 2, overflowX: "auto" }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            width={72}
                            height={36}
                            sx={{ flexShrink: 0, borderRadius: "999px" }}
                        />
                    ))}
                </Box>
            ) : (
                <LibraryCategoryPills
                    options={categoryOptions}
                    value={selectedTag}
                    onChange={selectTag}
                    snapshotMode={snapshotMode}
                />
            )}

            {loading ? (
                <Box sx={{ mb: 1 }} aria-busy="true" aria-live="polite">
                    <Typography sx={{ ...captionSx, color: tokens.textMuted, textAlign: "center", mb: 2 }}>
                        {t("native.library.loading", { defaultValue: "Loading puzzles…" })}
                    </Typography>
                    <Box sx={libraryPuzzleGridSx}>
                        {Array.from({ length: INITIAL_SKELETON_COUNT }, (_, i) => (
                            <LibraryCardSkeleton key={`skel-${i}`} />
                        ))}
                    </Box>
                </Box>
            ) : null}

            {!loading && errorKind && rows.length === 0 ? (
                <LibraryFeedState
                    variant={errorKind === "offline" ? "offline" : "error"}
                    title={
                        errorKind === "offline"
                            ? t("native.library.offlineTitle", { defaultValue: "You're offline" })
                            : t("native.library.errorTitle", { defaultValue: "Couldn't load library" })
                    }
                    body={error}
                    actionLabel={t("native.library.retry", { defaultValue: "Try again" })}
                    onAction={retryLoad}
                />
            ) : null}

            {!loading && !errorKind && rows.length === 0 ? (
                <Typography sx={{ ...captionSx, color: tokens.textMuted, textAlign: "center" }}>
                    {selectedTag
                        ? t("native.library.noPuzzlesTag", {
                              defaultValue: 'No puzzles tagged “{{tag}}”.',
                              tag: formatTagLabel({ tag: selectedTag }),
                          })
                        : t("native.library.noPuzzles", {
                              defaultValue: "No puzzles published yet.",
                          })}
                </Typography>
            ) : null}

            {!loading && rows.length > 0 ? (
                <Box
                    sx={{
                        ...libraryPuzzleGridSx,
                        mb: 1,
                    }}
                >
                    {rows.map((group, index) => {
                        const storyId = group.storyId ?? group?.story?.id;
                        const tpl = group?.templates?.[0];
                        const key = storyId || tpl?.id || `row-${index}`;
                        return (
                            <LibraryCard
                                key={key}
                                variant="browse"
                                group={group}
                                playing={
                                    Boolean(
                                        startingStoryId &&
                                            startingStoryId ===
                                                (group.storyId ?? group?.story?.id)
                                    )
                                }
                                onPlay={(opts) => startPlay(group, opts)}
                                freeTier={!isSubscribed}
                                savedGridPrefs={savedGridPrefs}
                            />
                        );
                    })}
                </Box>
            ) : null}

            <PaywallSheet
                open={showPaywallSheet}
                onClose={() => setShowPaywallSheet(false)}
            />

            {!loading && hasNext ? (
                <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} aria-hidden />
            ) : null}

            {loadingMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} sx={{ color: "#3BA666" }} />
                </Box>
            ) : null}

            {!loading && error && rows.length > 0 ? (
                <Box sx={{ textAlign: "center", mt: 1 }}>
                    <Typography sx={{ ...captionSx, color: "#f87171", mb: 1 }}>{error}</Typography>
                    <Typography
                        component="button"
                        type="button"
                        onClick={() => void loadNextPage()}
                        sx={{
                            ...captionSx,
                            color: "#3BA666",
                            fontWeight: 700,
                            border: "none",
                            bgcolor: "transparent",
                            cursor: "pointer",
                            minHeight: 44,
                            p: 0,
                        }}
                    >
                        {t("native.library.retry", { defaultValue: "Try again" })}
                    </Typography>
                </Box>
            ) : null}
            </Box>

        </PageShell>
    );
}
